import { identityColour } from "./identityColour";

/**
 * The colour one speaker is drawn in, everywhere they appear.
 *
 * The handoff colour-codes speaker names in the transcript, and the point of the
 * colour is recognition — the same person has to be the same colour in the segment
 * list, in the Speakers tab, and on whatever screen shows them next. So it is derived
 * from the speaker's position in the recording rather than assigned as they are
 * rendered: position is the one thing about a speaker that does not change while the
 * page is open, and it is already meaningful (M2.3 numbers speakers in the order they
 * first spoke).
 *
 * The ramp holds ten and then wraps; `identityColour` explains where the ten come from
 * and why they are ordered the way they are.
 */
export function speakerColour(index: number): string {
  return identityColour(index);
}

/**
 * What to call a speaker: the name somebody confirmed, else the position they were
 * numbered by.
 *
 * Never the diarizer's own label. `SPEAKER_00` is a cluster identifier and means
 * nothing to a reader — "Speaker 1" is the same fact said in the interface's own
 * words, and confirming who it actually is arrives in M4.2.
 */
export function speakerName(speaker: { confirmedName: string | null }, index: number): string {
  return speaker.confirmedName ?? `Speaker ${index + 1}`;
}

/** The two letters on a speaker's avatar — initials of a name, or the number. */
export function speakerInitials(speaker: { confirmedName: string | null }, index: number): string {
  if (!speaker.confirmedName) return `S${index + 1}`;

  const parts = speaker.confirmedName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return `S${index + 1}`;

  const letters = parts.length === 1 ? parts[0]!.slice(0, 2) : `${parts[0]![0]}${parts[parts.length - 1]![0]}`;

  return letters.toUpperCase();
}
